import { ArrowUpCircle, Package, RefreshCw } from 'lucide-react';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';

import { AddonsAddonListItem } from '@api/types.gen';

import { ExternalLink } from '@/react/components/ExternalLink';

import { ResourceDetailHeader } from '@@/ResourceDetailHeader/ResourceDetailHeader';
import { HeaderStats } from '@@/ResourceDetailHeader/HeaderStats';
import { ActionBarShell } from '@@/ResourceDetailHeader/ActionBarShell';
import { ActionBarButton } from '@@/ResourceDetailHeader/ActionBarButton';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { FallbackImage } from '@@/FallbackImage';
import { LinkButton } from '@@/LinkButton';

import { AddonVersionBadge } from '../AddonVersionBadge';
import { buildUpgradeParams, isUpgradeRequested } from '../upgradeDeepLink';
import { getAddonLifecycleState } from '../addonLifecycleState';

import { StatusStatBlock } from './StatusStatBlock';

interface Props {
  addon?: AddonsAddonListItem;
  isLoading: boolean;
  /** The uninstall mutation's own loading state, layered on top of the
   * lifecycle-derived one below (a click needs to show as loading before the
   * server-side lifecycleStatus has had a chance to move to "uninstalling"). */
  uninstallLoading: boolean;
  /** True when the add-on's Helm release could not be loaded (removed outside
   * Portainer, or a broken install/upgrade), so the header offers a reinstall
   * even when the add-on is still enabled. */
  releaseFailedToLoad?: boolean;
  onUninstall: () => void;
  /** Shown only once the Helm release has loaded, since a restart re-runs it. */
  showRestart?: boolean;
  restartLoading?: boolean;
  onRestart?: () => void;
}

// Lifecycle-driven action logic mirrors the list view (AddonCard): the copy
// and the disable/enable and hide/show rules match, only the action-bar
// styling differs.
export function AddonHeader({
  addon,
  isLoading,
  uninstallLoading,
  releaseFailedToLoad = false,
  onUninstall,
  showRestart = false,
  restartLoading = false,
  onRestart,
}: Props) {
  const router = useRouter();
  const { params: currentParams } = useCurrentStateAndParams();
  const displayName = addon?.displayName ?? 'Add-on';

  const {
    isFailed,
    isInstalling,
    isUninstalling,
    isMutating,
    upgradeFailed,
    canUninstall,
  } = getAddonLifecycleState(addon);
  const latestVersion = addon?.record?.availableVersions?.[0];
  // Treat failed load as reinstall-worthy once settled (not during mid-operation)
  const releaseUnavailable =
    releaseFailedToLoad && !isInstalling && !isMutating;
  const showUpgrade =
    !!latestVersion &&
    !isMutating &&
    !releaseUnavailable &&
    (!!addon?.record?.upgradeAvailable || upgradeFailed);
  const showInstall = isFailed || releaseUnavailable;

  const actionBar = addon ? (
    <ActionBarShell>
      <div className="flex items-center gap-3">
        {!!addon.enabled && (
          <>
            {/*
              TODO(R8S-1166): when healthStatus is "unhealthy", add a
              "Troubleshoot" action linking to the addon's logs/events.
            */}
            <ActionBarButton
              as={ExternalLink}
              props={{ to: addon.path || '' }}
              data-cy="addon-launch-link"
            >
              Launch
            </ActionBarButton>
            {showUpgrade && (
              <ActionBarButton
                icon={upgradeFailed ? RefreshCw : ArrowUpCircle}
                onClick={() =>
                  router.stateService.go('.', buildUpgradeParams(addon.id), {
                    // When already parked on that tab the params, reload to re-seed the version to handle repeat clicks.
                    reload:
                      currentParams.tab === 'configuration' &&
                      isUpgradeRequested(currentParams),
                  })
                }
                data-cy="addon-upgrade-button"
              >
                {upgradeFailed
                  ? 'Retry upgrade'
                  : `Upgrade to v${latestVersion}`}
              </ActionBarButton>
            )}
          </>
        )}
        {showRestart && (
          <ActionBarButton
            icon={RefreshCw}
            onClick={onRestart}
            // Disabled mid-upgrade/uninstall (racing the running operation) and
            // between the click and the lifecycle status catching up.
            disabled={isMutating || restartLoading}
            data-cy="addon-restart-button"
          >
            Restart
          </ActionBarButton>
        )}
        {showInstall && (
          // The install route doubles as the retry: a failed first install has
          // no live release, and a release that failed to load needs replacing.
          <ActionBarButton
            as={LinkButton}
            props={{ to: 'portainer.addons.install', params: { id: addon.id } }}
            icon={RefreshCw}
            data-cy="addon-install-button"
          >
            {isFailed ? 'Retry install' : 'Reinstall'}
          </ActionBarButton>
        )}
      </div>
      {canUninstall && (
        <div className="flex items-center gap-1">
          <DeleteButton
            confirmMessage={`Uninstall ${addon.displayName}?`}
            onConfirmed={onUninstall}
            disabled={isMutating}
            isLoading={isUninstalling || uninstallLoading}
            loadingText="Loading..."
            data-cy="addon-uninstall-button"
          >
            Uninstall
          </DeleteButton>
        </div>
      )}
    </ActionBarShell>
  ) : undefined;

  return (
    <ResourceDetailHeader
      icon={
        <FallbackImage
          fallbackIcon={<Package />}
          src={addon?.icon}
          alt={displayName}
          className="flex h-full w-full items-center justify-center rounded-lg object-contain"
        />
      }
      // The add-on logo fills the icon box, so drop the accent-coloured
      // tile behind it.
      iconBackgroundClassName="bg-transparent"
      subtitleLabel="Add-on"
      title={displayName}
      badge={
        addon?.record?.chartVersion ? (
          <AddonVersionBadge record={addon.record} />
        ) : undefined
      }
      description={addon?.description}
      rightInfo={
        addon && (
          <HeaderStats>
            <StatusStatBlock addon={addon} />
          </HeaderStats>
        )
      }
      actionBar={actionBar}
      isLoading={isLoading}
    />
  );
}
